import i18next from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdContentCopy, MdLabel, MdPeople } from "react-icons/md";
import { CopyRisksBetweenYears } from "../components/CopyRisksBetweenYears";
import { ManageCollaborators } from "../components/ManageCollaborators";
import { ManageTags } from "../components/ManageTags";
import { useDesignSystem } from "../context/designSystemContext";

type SettingsSection = "copy" | "collaborators" | "tags";

export const Settings = () => {
  const { designSystem } = useDesignSystem();
  useTranslation();
  const [activeSection, setActiveSection] = useState<SettingsSection>("copy");

  const navItems: { id: SettingsSection; label: string; icon: React.ReactNode }[] = [
    { id: "copy", label: i18next.t("copy_risks_section_title"), icon: <MdContentCopy className="w-5 h-5" /> },
    { id: "collaborators", label: i18next.t("collaborators_section_title"), icon: <MdPeople className="w-5 h-5" /> },
    { id: "tags", label: i18next.t("tags_section_title"), icon: <MdLabel className="w-5 h-5" /> },
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50/50">
      <header className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <h1
          className="font-openSans font-bold text-2xl"
          style={{ color: designSystem?.cssBasics.primaryColor }}
        >
          {i18next.t("settings_title")}
        </h1>
        <p className="font-openSans text-gray-600 mt-1 text-sm">
          {i18next.t("settings_description")}
        </p>
      </header>

      <div className="flex flex-1 min-h-0">
        <nav
          className="flex-shrink-0 w-56 bg-white border-r border-gray-200 flex flex-col py-4"
          aria-label={i18next.t("settings_title")}
        >
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveSection(item.id)}
              className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-lg font-openSans text-left text-sm transition-colors ${
                activeSection === item.id
                  ? "text-white"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
              style={
                activeSection === item.id
                  ? { backgroundColor: designSystem?.cssBasics.secondaryColor }
                  : undefined
              }
            >
              {item.icon}
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        <main className="flex-1 overflow-y-auto custom-scrollbar p-6">
          <div className="max-w-4xl mx-auto">
            {activeSection === "copy" && (
              <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2
                  className="font-openSans font-semibold text-xl mb-2"
                  style={{ color: designSystem?.cssBasics.primaryColor }}
                >
                  {i18next.t("copy_risks_section_title")}
                </h2>
                <p className="font-openSans text-gray-600 mb-6 text-sm">
                  {i18next.t("copy_risks_section_description")}
                </p>
                <CopyRisksBetweenYears />
              </section>
            )}

            {activeSection === "collaborators" && (
              <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2
                  className="font-openSans font-semibold text-xl mb-2"
                  style={{ color: designSystem?.cssBasics.primaryColor }}
                >
                  {i18next.t("collaborators_section_title")}
                </h2>
                <p className="font-openSans text-gray-600 mb-6 text-sm">
                  {i18next.t("collaborators_section_description")}
                </p>
                <ManageCollaborators />
              </section>
            )}

            {activeSection === "tags" && (
              <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2
                  className="font-openSans font-semibold text-xl mb-2"
                  style={{ color: designSystem?.cssBasics.primaryColor }}
                >
                  {i18next.t("tags_section_title")}
                </h2>
                <p className="font-openSans text-gray-600 mb-6 text-sm">
                  {i18next.t("tags_section_description")}
                </p>
                <ManageTags />
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};
